import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/browser';
import api from './client';
import type { ApiResponse } from '../types/common';
import type { User, Passkey } from '../types/auth';

export const authApi = {
  register: (data: { email: string; password: string; firstName: string; lastName: string; inviteToken?: string }) =>
    api.post<ApiResponse<{ user: User }>>('/auth/register', data),

  login: (data: { email: string; password: string }) =>
    api.post<ApiResponse<{ user?: User; requiresTotp?: boolean }>>('/auth/login', data),

  logout: () => api.post<ApiResponse<void>>('/auth/logout'),

  me: () => api.get<ApiResponse<{ user: User }>>('/auth/me'),

  forgotPassword: (email: string) =>
    api.post<ApiResponse<void>>('/auth/forgot-password', { email }),

  resetPassword: (token: string, password: string) =>
    api.post<ApiResponse<void>>('/auth/reset-password', { token, password }),

  // TOTP
  totpSetup: () => api.post<ApiResponse<{ secret: string; otpauthUrl: string }>>('/auth/totp/setup'),
  totpVerify: (token: string) => api.post<ApiResponse<{ message: string; backupCodes: string[] }>>('/auth/totp/verify', { token }),
  totpValidate: (token: string) => api.post<ApiResponse<{ user: User }>>('/auth/totp/validate', { token }),
  totpDisable: (token: string) => api.post<ApiResponse<void>>('/auth/totp/disable', { token }),

  // Backup Codes
  backupCodeValidate: (code: string) => api.post<ApiResponse<{ user: User }>>('/auth/backup-code/validate', { code }),
  backupCodesStatus: () => api.get<ApiResponse<{ totpEnabled: boolean; remainingCodes: number }>>('/auth/backup-codes/status'),
  backupCodesRegenerate: (token: string) => api.post<ApiResponse<{ message: string; backupCodes: string[] }>>('/auth/backup-codes/regenerate', { token }),

  // Passkeys
  listPasskeys: () => api.get<ApiResponse<{ passkeys: Passkey[] }>>('/auth/passkeys'),
  passkeyRegisterOptions: () => api.post<ApiResponse<PublicKeyCredentialCreationOptionsJSON>>('/auth/passkey/register/options'),
  passkeyRegisterVerify: (response: RegistrationResponseJSON, name?: string) =>
    api.post<ApiResponse<void>>('/auth/passkey/register/verify', { response, name }),
  passkeyLoginOptions: () => api.post<ApiResponse<PublicKeyCredentialRequestOptionsJSON>>('/auth/passkey/login/options'),
  passkeyLoginVerify: (response: AuthenticationResponseJSON) =>
    api.post<ApiResponse<{ user: User }>>('/auth/passkey/login/verify', { response }),
  deletePasskey: (id: string) => api.delete<ApiResponse<void>>(`/auth/passkeys/${id}`),
};
